/**
 * NEW_PLANS_FLAG
 *
 * Centralized feature flag for the new plans UI (PlansCards, UpgradeFeatureModal,
 * UpgradeWindow, UpgradePlanSuccessModal, new account-creation plan/payment pages).
 *
 * Gate: both `beta_tester` AND `beta_tester_internal` user flags must be true.
 *
 * To launch to all users:
 *   1. Search for "isNewPlans" / "isNewPlansEnabled" across the codebase
 *   2. Remove all conditional branches, keeping only the "new plans" code
 *   3. Delete this file
 *   4. Remove legacy account-creation pages (src/views/Auth/AccountCreation/legacy/)
 */

import { computed } from 'vue'
import { useUserStore } from '@/stores/UserStore'

// On localhost we treat every gated feature as enabled, so devs can work on
// flagged surfaces (Orders, the upgraded Sale Pick List, etc.) without the BE
// flag set on their account. Real environments still honour the flag.
export const isLocalhost = () =>
  typeof window !== 'undefined' && window.location?.hostname === 'localhost'

export const useNewPlans = () => {
  const userStore = useUserStore()
  return computed(() => !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal))
}

export const isNewPlansEnabled = () => {
  const userStore = useUserStore()
  return !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal)
}

/**
 * Bulk End / Relist  — PublishCardsMenu footer actions.
 * Gate: `user.flags['bulk-delist']` (also accepts `bulk_delist`).
 * Localhost always on so the flow is testable without the BE flag.
 */
export const useBulkDelist = () => {
  const userStore = useUserStore()
  return computed(() =>
    isLocalhost()
    || !!(userStore.flags?.['bulk-delist'] || userStore.flags?.bulk_delist)
  )
}

/**
 * Inventory CSV import  — "Import from CSV" in Import Products menu.
 * Gate: `user.flags['csv-import']` (also accepts `csv_import`).
 * Localhost always on so the flow is testable without the BE flag.
 * Plan entitlement (`csv-inventory-import`) still lives inside the modal.
 */
export const useCsvImport = () => {
  const userStore = useUserStore()
  return computed(() =>
    isLocalhost()
    || !!(userStore.flags?.['csv-import'] || userStore.flags?.csv_import)
  )
}
